import { DiffType } from '@ardoq/data-model';
import { GraphItem, type GraphItemMetadata } from './GraphItem';
import SparkMD5 from 'spark-md5';
import { ComponentRepresentationDataWithColorShades } from '@ardoq/basic-components';
import { getGraphItemLabel } from './getGraphItemLabel';

type MetaModelViewNodeMetadata = {
  representationData: ComponentRepresentationDataWithColorShades;
  color: string;
  typeId: string;
  label: string;
  labelWithCount?: string;
  hasFilters?: boolean;
  isContext?: boolean;
  shouldDisplayClickOtherComponentsHint?: boolean;
  /** Optional hover tooltip text. */
  tooltip?: string;
};

type GraphNodeConstructorArgs = {
  modelId: string;
  id?: string;
  metaData: MetaModelViewNodeMetadata;
};
export class MetaModelViewGraphNode extends GraphItem {
  static create(
    modelId: string,
    id: string,
    metaData: MetaModelViewNodeMetadata & {
      representationData: ComponentRepresentationDataWithColorShades;
    }
  ): MetaModelViewGraphNode {
    return new MetaModelViewGraphNode({
      modelId,
      id,
      metaData,
    });
  }

  metaData: MetaModelViewNodeMetadata;
  private isSelected = false;
  private isInSelectedPath = false;

  private constructor({ modelId, id, metaData }: GraphNodeConstructorArgs) {
    const graphItemMetadata: GraphItemMetadata = {
      isComponent: false,
      isReference: false,
      isGroup: false,
    };
    super(modelId, graphItemMetadata, id);
    this.metaData = metaData;

    this.hash = SparkMD5.hash(this.getRawHash());
  }
  override getVisualDiffType(): DiffType {
    return DiffType.NONE;
  }

  // #region compatibility with the base class and the reused legacy code
  override setLabel(label: string): void {
    this.metaData.label = label;
  }
  override getCSS(): string {
    return '';
  }
  override getItemLabels() {
    return undefined;
  }
  override setItemLabels(): void {}
  override hasURLFields(): boolean {
    return false;
  }
  // #endregion
  override getLabel() {
    return getGraphItemLabel(this.metaData.label);
  }
  override getLabelWithCount() {
    return getGraphItemLabel(this.metaData.labelWithCount || this.getLabel());
  }

  setMetaData(metaData: MetaModelViewNodeMetadata) {
    this.metaData = metaData;
  }
  getMetadata() {
    return this.metaData;
  }

  hasFilters() {
    return this.metaData.hasFilters || false;
  }

  isContext() {
    return this.metaData.isContext;
  }

  shouldDisplayClickOtherComponentsHint() {
    return this.metaData?.shouldDisplayClickOtherComponentsHint || false;
  }

  getRepresentationData() {
    return this.metaData.representationData ?? null;
  }

  getImage() {
    const representationData = this.metaData.representationData;

    if (!representationData || !representationData.isImage) {
      return '';
    }

    return this.metaData.representationData?.value ?? '';
  }

  getColor() {
    return this.metaData.color;
  }

  getTooltip() {
    return this.metaData.tooltip;
  }

  getTypeId() {
    return this.metaData.typeId ?? 'unknown';
  }

  setIsSelected(isSelected: boolean) {
    this.isSelected = isSelected;
  }

  getIsSelected() {
    return this.isSelected;
  }

  setIsInSelectedPath(isSelected: boolean) {
    this.isInSelectedPath = isSelected;
  }

  getIsInSelectedPath() {
    return this.isInSelectedPath;
  }
}
